import { getBookingStartingPoint } from '@/helpers/analytics';
import { getScreenNameFromPath, trackMpEvent } from '@/helpers/analyticsTS';
import { bookingFlowTrackingSession } from '@/helpers/checkoutFlowTrackingSession';
import { Place } from '@/types/state/shared';
import { User } from '@/types/user';
import { BOOKING_FLOW_STEP_SLUGS, BookingFlowStep } from './constants';
import { getStepFromPathname } from './goToStep';

/** Step-shown for a booking-flow step page (U15): the standard `screen_shown`, with the screen name derived from the route (U11). */
export function trackStepShown(pathname: string): void {
  trackMpEvent({ name: 'screen_shown', properties: { screen_name: getScreenNameFromPath(pathname) } });
}

/**
 * Calendar-page parity (U15): a step reached with no active booking_flow session — deep
 * link, new tab, browser-forward after a cancel, or a session another page already ended —
 * starts one. `initiated_from` prefers the stored booking starting point (sessionStorage,
 * survives cancelSession), so a re-entry reports the same origin as the first initiate;
 * only a visit with no stored origin falls back to the step's own screen name, like the
 * calendar's own `!isActive()` guard. Without this every later session event is dropped,
 * with a Sentry message apiece.
 */
export function ensureTrackingSession(
  place: Pick<Place, 'id' | 'about' | 'lastMinuteDiscount' | 'campaigns'>,
  user: User | null,
  pathname: string,
): void {
  // A leftover session from another place is re-initiated, never adopted — initiate() ends it.
  if (bookingFlowTrackingSession.isActive() && bookingFlowTrackingSession.getCompanyId() === place.id) return;
  bookingFlowTrackingSession.initiate(place, user, getBookingStartingPoint() || getScreenNameFromPath(pathname));
}

/**
 * Modal parity (U15): `setProperties({ service_ids })` before the step event, so the event
 * already carries the updated selection — and so it sticks to every later event through
 * checkout_confirmation. `serviceIds` is always the full post-change selection as strings.
 */
export function trackServiceSelected(serviceIds: string[]): void {
  bookingFlowTrackingSession.setProperties({ service_ids: serviceIds });
  bookingFlowTrackingSession.serviceSelected();
}

export function trackAddOnAdded(serviceIds: string[]): void {
  bookingFlowTrackingSession.setProperties({ service_ids: serviceIds });
  bookingFlowTrackingSession.addRecommendedService();
}

export function trackAddOnRemoved(serviceIds: string[]): void {
  bookingFlowTrackingSession.setProperties({ service_ids: serviceIds });
  bookingFlowTrackingSession.removedService();
}

/**
 * The drawer's add-service funnel, entered from either summary link — "Rekommenderade
 * tillval" or "Lägg till fler tjänster": opening the list is the attempt, leaving it without
 * a pick the abandonment. The success leg is trackMainServiceAdded, which also refreshes
 * service_ids. Modal parity holds for the main-service link (SummaryView reports the same
 * legs); the control arm renders recommended add-ons inline, so it has no open leg for them
 * and its initiated count runs lower than this arm's.
 */
export function trackAddService(step: 'initiated' | 'cancel'): void {
  bookingFlowTrackingSession.addService(step);
}

/**
 * A service appended from either drawer list, main or recommended add-on. The modal reports
 * a main added this way as add_service/success rather than service_selected
 * (handleOnPickService's step-category branch), with service_ids refreshed first so the
 * event already carries the new selection. trackAddOnAdded stays the page-level list's
 * event; a recommended add-on picked inside the drawer belongs to this funnel instead.
 */
export function trackMainServiceAdded(serviceIds: string[]): void {
  bookingFlowTrackingSession.setProperties({ service_ids: serviceIds });
  bookingFlowTrackingSession.addService('success');
}

export function trackEmployeeSelected(employeeSelection: 'specific' | 'any'): void {
  bookingFlowTrackingSession.employeeSelected(employeeSelection);
}

/**
 * The service details modal opening ('initiated') / closing ('cancel'). Modal parity:
 * the control arm emits 'cancel' on every close, including the close a pick triggers, and
 * hardcodes bookable=true — the step pages only list bookable services.
 */
export function trackServiceDetailsViewed(step: 'initiated' | 'cancel'): void {
  bookingFlowTrackingSession.viewServiceDetails(step, true);
}

/** The list-view wiring for trackServiceDetailsViewed, spread into every step page's list hooks. */
export const serviceDetailsTrackingHandlers = {
  onShowServiceInfo: () => trackServiceDetailsViewed('initiated'),
  onHideServiceInfo: () => trackServiceDetailsViewed('cancel'),
};

/** The SelectionDrawer wiring for trackAddService's open and abandon legs, spread onto every host's drawer. */
export const addServiceTrackingHandlers = {
  onOpenAddService: () => trackAddService('initiated'),
  onCancelAddService: () => trackAddService('cancel'),
};

/**
 * Sticky-props-only update, no event: the extra-time "yes" answer appends add-ons that
 * checkout_confirmation must carry in `service_ids`, but the control arm fires no event for
 * the answer itself.
 */
export function updateTrackedServiceIds(serviceIds: string[]): void {
  bookingFlowTrackingSession.setProperties({ service_ids: serviceIds });
}

/**
 * Hydration parity (U15): a rewind, a same-step browser back or a calendar-back changes the
 * selection through the URL with no click handler, so the sticky `service_ids` the last
 * click set would keep naming services the booking no longer holds — through
 * checkout_confirmation. Called after every hydration with what it materialised. A render
 * with no session (a deep link before the shell's fallback initiate) has nothing to update.
 */
export function syncTrackedServiceIds(services: readonly { id?: number | null }[]): void {
  if (!bookingFlowTrackingSession.isActive()) return;
  bookingFlowTrackingSession.setProperties({
    service_ids: services.flatMap((service) => (typeof service.id === 'number' ? [String(service.id)] : [])),
  });
}

const STEP_ORDER: readonly BookingFlowStep[] = [
  BOOKING_FLOW_STEP_SLUGS.SELECT_SERVICE,
  BOOKING_FLOW_STEP_SLUGS.SELECT_EMPLOYEE,
  BOOKING_FLOW_STEP_SLUGS.ADD_ONS,
  BOOKING_FLOW_STEP_SLUGS.EXTRA_TIME,
];

/**
 * POP destinations that continue the flow forwards; landing on one is a redo, not an exit.
 * History v4 gives no direction, so a back-jump onto an OLDER such entry also reads as
 * forward and skips the cancel — accepted limitation.
 */
const FORWARD_EXIT_PREFIXES = ['/boka-tjanst/', '/booking/'];

/** Whether a POP destination continues the flow forwards (calendar, checkout) rather than leaving it. */
export function isForwardExitPathname(pathname: string): boolean {
  return FORWARD_EXIT_PREFIXES.some((prefix) => pathname.startsWith(prefix));
}

/**
 * Called by the calendar's POP guard before its blanket cancel: a POP landing on a flow
 * step is a rewind — emit the in-flow browser-back rule and return true so the caller
 * skips cancelSession(). Control histories never contain step URLs.
 */
export function trackCalendarPopToStep(toPathname: string): boolean {
  const toStep = getStepFromPathname(toPathname);
  if (!toStep) return false;
  if (!bookingFlowTrackingSession.isActive()) return true;
  emitRewind(toStep);
  return true;
}

/**
 * Browser-back parity with the header back button (U15): a backwards POP out of the flow
 * cancels the session, a POP to an earlier step emits the header's back event. Direction
 * comes from step order — history v4 cannot tell back from forward — so forward POPs emit nothing.
 */
export function trackFlowPopNavigation(fromPathname: string, toPathname: string): void {
  if (!bookingFlowTrackingSession.isActive()) return;
  const fromStep = getStepFromPathname(fromPathname);
  if (!fromStep) return;
  const toStep = getStepFromPathname(toPathname);
  if (!toStep) {
    if (isForwardExitPathname(toPathname)) return;
    bookingFlowTrackingSession.cancelSession();
    return;
  }
  if (STEP_ORDER.indexOf(toStep) >= STEP_ORDER.indexOf(fromStep)) return;
  emitRewind(toStep);
}

/** The one rewind rule, shared by header-back, in-flow browser-back and calendar-back. */
export function emitRewind(toStep: BookingFlowStep): void {
  if (toStep === BOOKING_FLOW_STEP_SLUGS.SELECT_SERVICE) bookingFlowTrackingSession.backToServiceSelection();
  if (toStep === BOOKING_FLOW_STEP_SLUGS.SELECT_EMPLOYEE) bookingFlowTrackingSession.backToEmployeeSelection();
  if (toStep === BOOKING_FLOW_STEP_SLUGS.ADD_ONS) bookingFlowTrackingSession.backToAddOnSelection();
}
